import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Wordmark from "./Wordmark";
import RegionPicker from "./RegionPicker";

const NAV_LINKS = [
    { label: "Platform", href: "#platform", testid: "nav-link-platform" },
    {
        label: "Maturity method",
        href: "#maturity",
        testid: "nav-link-maturity",
    },
    { label: "Workflows", href: "#workflows", testid: "nav-link-workflows" },
    { label: "Pricing", href: "#pricing", testid: "nav-link-pricing" },
    {
        label: "Documentation",
        href: "#documentation",
        testid: "nav-link-documentation",
    },
];

/**
 * Sticky top navigation. Dark surface with hairline bottom border.
 * Anchor links scroll to landing-page sections; when not on the landing,
 * they navigate to /#section.
 */
export const Nav = () => {
    const location = useLocation();
    const onLanding = location.pathname === "/";
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const buildAnchorHref = (hash) => (onLanding ? hash : `/${hash}`);

    return (
        <header
            data-testid="site-nav"
            className="sticky top-0 z-50 w-full bg-[#0e1416] text-[#e6ebec] border-b border-[#1a2124]"
        >
            <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
                <div className="flex h-[64px] items-center justify-between">
                    {/* Left: wordmark */}
                    <div className="flex items-center gap-5">
                        <Link
                            to="/"
                            data-testid="nav-wordmark-link"
                            className="flex items-baseline gap-3"
                        >
                            <Wordmark variant="dark" size="text-[17px]" />
                        </Link>
                    </div>

                    {/* Right: links (desktop) */}
                    <nav className="hidden lg:flex items-center gap-7">
                        {NAV_LINKS.map((link) => (
                            <a
                                key={link.label}
                                href={buildAnchorHref(link.href)}
                                data-testid={link.testid}
                                className="text-[13px] text-[#8a9498] hover:text-[#e6ebec] transition-colors duration-200"
                            >
                                {link.label}
                            </a>
                        ))}
                        <RegionPicker placement="nav" />
                        <Link
                            to="/login"
                            data-testid="nav-sign-in"
                            className="text-[13px] text-[#8a9498] hover:text-[#e6ebec] transition-colors duration-200"
                        >
                            Sign in
                        </Link>
                        <Link
                            to="/app"
                            data-testid="nav-launch-app"
                            className="group inline-flex items-center gap-1.5 text-[13px] text-[#01696f] hover:text-[#2a888d] transition-colors duration-200"
                        >
                            Launch App
                            <ArrowUpRight
                                size={14}
                                strokeWidth={1.5}
                                className="transition-transform duration-200 group-hover:translate-x-[1px] group-hover:-translate-y-[1px]"
                            />
                        </Link>
                    </nav>

                    {/* Mobile menu trigger */}
                    <button
                        type="button"
                        data-testid="nav-mobile-toggle"
                        aria-label={open ? "Close menu" : "Open menu"}
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden inline-flex items-center justify-center w-9 h-9 -mr-1 text-[#e6ebec] hover:text-[#01696f] transition-colors"
                    >
                        {open ? (
                            <X size={18} strokeWidth={1.5} />
                        ) : (
                            <Menu size={18} strokeWidth={1.5} />
                        )}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-panel"
                    className="lg:hidden border-t border-[#1a2124] bg-[#0e1416]"
                >
                    <div className="mx-auto max-w-[1280px] px-6 py-4 flex flex-col gap-1">
                        {NAV_LINKS.map((link) => (
                            <a
                                key={link.label}
                                href={buildAnchorHref(link.href)}
                                data-testid={`${link.testid}-mobile`}
                                className="py-2.5 text-[14px] text-[#8a9498] hover:text-[#e6ebec] transition-colors"
                            >
                                {link.label}
                            </a>
                        ))}
                        <div className="py-2.5 flex items-center justify-between border-t border-[#1a2124] mt-1 pt-3">
                            <span className="tc-mono text-[10px] text-[#5a6669] uppercase tracking-wider">
                                Region
                            </span>
                            <RegionPicker placement="nav" />
                        </div>
                        <Link
                            to="/login"
                            data-testid="nav-sign-in-mobile"
                            className="py-2.5 text-[14px] text-[#8a9498]"
                        >
                            Sign in
                        </Link>
                        <Link
                            to="/app"
                            data-testid="nav-launch-app-mobile"
                            className="py-2.5 text-[14px] text-[#01696f]"
                        >
                            Launch App →
                        </Link>
                        <div className="pt-3 mt-2 border-t border-[#1a2124] tc-mono text-[10px] text-[#5a6669] leading-[1.45]">
                            www.concrete-control.com
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Nav;
